import React, { useEffect, useState } from 'react';
import { Controller, useController, UseControllerProps } from 'react-hook-form';
import { Form, Icon, InputProps, Label, Loader } from 'semantic-ui-react';
import { getLabel } from './util';
import styled, { CSSProperties } from 'styled-components'
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-lodash-debounce';
import AuthService from '~/api/services/auth';
import { emailRegex } from './constants';
import CurrencyInput from 'react-currency-input-field';
import { handleErrors } from '~/utils/helpers';
import { useAuth } from '~/routes/components/AuthProvider';

const EmailInputWrapper = styled.div`
 width: 100%;
 padding-left: .5em;
 padding-right: .5em;
 .loader {
  right: 0px !important;
  margin-left: auto;
 }
`
interface InputFormFiled extends UseControllerProps {
  label?: string | JSX.Element;
  value?: string;
  control: any;
  parent?: string;
  hideError?: boolean;
  onChange?: any,
  noErrorMessage?: boolean,
  handleEmailCheck?: (correctEmail: boolean) => void
  typeInput?: string
  allowDecimals?: boolean
  decimalScale?: number
  withoutAsterisk?: boolean
  numberOfDecimals?: number
  disableScroolIntoView?: boolean
  styleLabel?: CSSProperties
  pointingErrorLabel?: boolean | 'above' | 'below' | 'left' | 'right'
  max?: number
  suffix?: string
  formatNumberForDb?: boolean
  prefix?: string
  styleControlled?: CSSProperties
  styleErrorLabel?: CSSProperties
  styleEmailWrapper?: CSSProperties
}

type InputFormFiledProps = InputFormFiled & InputProps;

const Input: React.FC<InputFormFiledProps> = (props) => {
  const { systemData } = useAuth()
  const { field, fieldState } = useController(props);
  const fieldTemp = { ...field, ref: null as any };
  const [correctEmail, setCorrectEmail] = useState(false)
  const debouncedEmailValue = useDebounce(field.value, 300);
  const [loadingEmailCheck, setLoadingEmailCheck] = useState(false)
  const { t } = useTranslation()
  const [suffix, setSuffix] = useState(props.suffix)

  const {
    control,
    label,
    id,
    rules,
    hideError,
    type,
    styleLabel,
    styleControlled,
    withoutAsterisk,
    typeInput,
    noErrorMessage,
    allowDecimals,
    styleEmailWrapper,
    disableScroolIntoView,
    decimalScale,
    handleEmailCheck,
    ...inputProps
  } = props;

  function notInViewport(element: any, offset = 0) {
    if (!element) return false
    const top = element.getBoundingClientRect().top
    return ((top + offset) >= 0 && (top - offset + 100) >= window.innerHeight) || (top - 20 <= 0)
  }

  useEffect(() => {
    if (fieldState.error && !disableScroolIntoView) {
      const element = document.getElementById(fieldTemp.name);
      if (notInViewport(element)) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
      }
    }
  }, [fieldState, props.rules]);

  useEffect(() => {
    if (debouncedEmailValue && typeInput === 'emailField' && fieldState.isDirty) {
      checkEmail(debouncedEmailValue)
    }
  }, [debouncedEmailValue])

  function renderInput() {
    return <Form.Input
      id={id || `${fieldTemp.name}`}
      {...inputProps}
      {...fieldTemp}
      autoComplete={fieldTemp.name.includes('email') ? 'nofill' : 'off'}
      onChange={(e, data) => {
        if (props.onChange) {
          props.onChange(e, data)
        }
        fieldTemp.onChange(data.value)
      }}
      type={type}
      onWheel={(e) => e.target.blur()}
      value={props.value ? props.value : fieldTemp.value || ''}
      error={Boolean(fieldState.error)}
      label={props.label ? getLabel(label, rules, fieldState) : null}
    />
  }

  async function checkEmail(value) {
    setLoadingEmailCheck(true)
    try {
      const response = await AuthService.checkEmail({ email: value })
      if (response.data === 1) {
        handleEmailCheck(emailRegex.test(value))
        setCorrectEmail(emailRegex.test(value))
      } else {
        handleEmailCheck(false)
        setCorrectEmail(false)
      }
    } catch (error) {
      handleErrors(error)
    }
    setLoadingEmailCheck(false)
  }

  function renderEmailInput() {
    const inputValue = props.value ? props.value : fieldTemp.value || ''
    return <EmailInputWrapper style={styleEmailWrapper}>
      <Form.Input
        id={`${fieldTemp.name}`}
        icon
        {...inputProps}
        {...fieldTemp}
        onChange={(e, data) => {
          if (props.onChange) {
            props.onChange(e, data)
          }
          fieldTemp.onChange(data.value)
        }}
        onWheel={(e) => e.target.blur()}
        value={props.value ? props.value : fieldTemp.value || ''}
        error={Boolean(fieldState.error)}
        label={props.label ? getLabel(label, rules, fieldState) : null}
      >
        <input />
        {loadingEmailCheck && <Loader active={true} size={'small'} />}
        {!loadingEmailCheck && inputValue !== '' && fieldState.isDirty && <Icon
          style={{ marginLeft: 10 }}
          name={correctEmail ? 'checkmark' : 'close'}
          color={correctEmail ? 'green' : 'red'}
        />}
      </Form.Input>
    </EmailInputWrapper>
  }

  function renderControledInput() {
    const isSwissDairy = systemData?.chEnv === 1
    return <Form.Field
      error={fieldState.error}
      required={!withoutAsterisk ? Boolean(props.rules?.required) : false}
      {...inputProps}
    >
      <label style={{ ...styleLabel }}>{props.label}</label>
      <Controller
        rules={props.rules}
        control={control}
        name={props.name}
        render={({ field: { onChange, onBlur, value, ref } }) => {
          return (
            <CurrencyInput
              intlConfig={{ locale: isSwissDairy ? 'de-CH' : 'de-DE' }}
              suffix={suffix}
              allowDecimals={Boolean(allowDecimals)}
              decimalScale={allowDecimals ? decimalScale || 2 : 0}
              disabled={props.disabled}
              prefix={props.prefix}
              style={{ ...styleControlled }}
              onFocus={() => setSuffix('')}
              decimalsLimit={allowDecimals ? decimalScale || 2 : 0}
              id={field.name}
              disableAbbreviations
              onValueChange={(value) => {
                if (!value) {
                  // setClassName('error');
                  onChange('');
                  return;
                }
                onChange(value)
              }}
              onBlur={() => {
                setSuffix(props.suffix)
                if (props.formatNumberForDb) {
                  onChange(value.replace(',', '.'))
                }
              }}
              max={props.max}
              placeholder={props.placeholder || t('Please enter a number')}
              step={1}
              value={value}
            />
          )
        }}
      />
      {fieldState?.error && !noErrorMessage && <Label prompt pointing={'above'} style={{ ...props.styleErrorLabel }}>{fieldState.error?.message}</Label>}
    </Form.Field>
  }

  function renderControlledErrorInput() {
    return <Form.Field
      error={fieldState.error}
      required={!withoutAsterisk ? props.rules?.required : false}
      {...inputProps}
    >
      <label style={{ ...styleLabel }}>{props.label}</label>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Controller
          rules={props.rules}
          control={control}
          name={props.name}
          render={({ field: { onChange, onBlur, value, ref } }) => {
            return (
              <CurrencyInput
                intlConfig={{ locale: 'de-DE' }}
                suffix={suffix}
                allowDecimals={Boolean(allowDecimals)}
                decimalScale={allowDecimals ? decimalScale || 2 : 0}
                disabled={props.disabled}
                prefix={props.prefix}
                style={{ ...styleControlled }}
                onFocus={() => setSuffix('')}
                decimalsLimit={allowDecimals ? decimalScale || 2 : 0}
                id={field.name}
                disableAbbreviations
                onValueChange={(value) => {
                  if (!value) {
                    // setClassName('error');
                    onChange('');
                    return;
                  }
                  onChange(value)
                }}
                onBlur={() => {
                  setSuffix(props.suffix)
                  if (props.formatNumberForDb) {
                    onChange(value.replace(',', '.'))
                  }
                }}
                max={props.max}
                placeholder={props.placeholder || t('Please enter a number')}
                step={1}
                value={value}
              />
            )
          }}
        />
        {fieldState?.error && !noErrorMessage && <Label
          prompt
          pointing={props?.pointingErrorLabel || 'above'}
          style={{ ...props.styleErrorLabel }}>{fieldState.error?.message}</Label>
        }
      </div>
    </Form.Field>
  }

  switch (typeInput) {
    case 'emailField':
      return renderEmailInput()
    case 'controled':
      return renderControledInput()
    case 'controlled-error-input':
      return renderControlledErrorInput()
    default:
      renderInput()
  }

  return renderInput()
};
export default Input;
